import { useTenant } from "@/contexts/TenantContext";
import { format, parseISO } from "date-fns";
import { formatInTimeZone, toZonedTime, fromZonedTime } from "date-fns-tz";

/**
 * Hook to format dates according to the tenant's timezone.
 *
 * Contract for input strings:
 *   - Strings with an explicit timezone designator (`Z` or `±HH:MM`, case-insensitive,
 *     trailing) are parsed as absolute instants via `parseISO`.
 *   - Naive datetime strings (no timezone designator, e.g. `"2026-08-12T07:00"`) are
 *     interpreted as **tenant-local wall-clock time** via `fromZonedTime`, NOT as
 *     browser-local time. This matches how the app writes those values
 *     (`custom_values.stops.*.time_window.*` in `logistics_requests`).
 *   - During DST transitions this can be ambiguous for tenants in DST-observing
 *     timezones; the tenant timezone is authoritative and `fromZonedTime` picks a
 *     single instant. `Asia/Singapore` (the current default) has no DST.
 */
const ISO_TZ_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/i;

export const useTenantTimezone = () => {
  const { currentTenant } = useTenant();
  const timezone = currentTenant?.timezone || 'Asia/Singapore';

  // Turn a string or Date input into an absolute instant, using the contract above.
  const parseToInstant = (value: string | Date): Date => {
    if (value instanceof Date) return value;
    const trimmed = value.trim();
    if (!trimmed) return new Date(NaN); // preserve existing invalid-input behavior
    return ISO_TZ_SUFFIX.test(trimmed)
      ? parseISO(trimmed)
      : fromZonedTime(trimmed, timezone);
  };

  const formatInTenantTimezone = (date: string | Date, formatString: string = 'PPP') => {
    const dateObj = parseToInstant(date);
    return formatInTimeZone(dateObj, timezone, formatString);
  };

  const formatTimeInTenantTimezone = (date: string | Date, formatString: string = 'HH:mm') => {
    const dateObj = parseToInstant(date);
    return formatInTimeZone(dateObj, timezone, formatString);
  };

  const formatDateTimeInTenantTimezone = (date: string | Date, formatString: string = 'PPP HH:mm') => {
    const dateObj = parseToInstant(date);
    return formatInTimeZone(dateObj, timezone, formatString);
  };

  const toTenantTimezone = (date: string | Date) => {
    const dateObj = parseToInstant(date);
    return toZonedTime(dateObj, timezone);
  };

  const getCurrentTimeInTenantTimezone = () => {
    return toZonedTime(new Date(), timezone);
  };

  const formatCurrentDateInTenantTimezone = (formatString: string = 'MMM d, yyyy') => {
    return formatInTimeZone(new Date(), timezone, formatString);
  };

  // Helper to parse date strings as midnight in tenant timezone
  const parseDateInTenantTimezone = (dateString: string): Date => {
    // Takes a date string like "2025-11-15" and interprets it as midnight in the tenant's timezone
    return fromZonedTime(`${dateString}T00:00:00`, timezone);
  };

  // Returns UTC ISO bounds for the tenant's current day (inclusive)
  const getTenantDayBoundsUtc = (date?: string | Date) => {
    let base: Date;
    
    if (!date) {
      base = new Date();
    } else if (typeof date === 'string') {
      // Use timezone-aware parser for string dates
      base = parseDateInTenantTimezone(date);
    } else {
      base = date;
    }
    
    const dayStr = formatInTimeZone(base, timezone, 'yyyy-MM-dd');
    const startUtc = fromZonedTime(`${dayStr}T00:00:00`, timezone);
    const endUtc = fromZonedTime(`${dayStr}T23:59:59.999`, timezone);
    return {
      startUtc,
      endUtc,
      startUtcIso: startUtc.toISOString(),
      endUtcIso: endUtc.toISOString(),
      day: dayStr,
    };
  };
  return {
    timezone,
    parseToInstant,
    formatInTenantTimezone,
    formatTimeInTenantTimezone,
    formatDateTimeInTenantTimezone,
    toTenantTimezone,
    getCurrentTimeInTenantTimezone,
    formatCurrentDateInTenantTimezone,
    parseDateInTenantTimezone,
    getTenantDayBoundsUtc,
  };

};